// Build-time image manifest. `vite-imagetools` (registered in vite.config.ts) transforms the
// `import.meta.glob` queries below into responsive srcsets at build time. Maps are keyed by
// filename stem (e.g. `home`, `k12-form`, `mark-desch`) for lookup from <AppImage> / <Banner>.
//
// Widths — 480;960;1440;1920;2560. The 2560 top end covers a full-bleed banner at 2× device pixel
// ratio (the safeguard against the fuzziness prior Astro/sharp deploys produced from under-sized
// variants); sharp does not upscale, so smaller sources (team headshots) cap at native width.
//
// Formats — AVIF as the primary `<source>` (no WebP: its 4:2:0 subsampling softens edges) plus a
// same-format fallback for the `<img>`. The fallback query intentionally omits `format` so source
// format is preserved (jpg stays jpg, png stays png with alpha — load-bearing for the transparent
// home/college-student.png overlay).
//
// Quality — AVIF q85 is the "visually lossless" threshold for photos (the 2560w banner variant
// lands ~190KB, still ~64% smaller than its 528KB source JPEG). We let sharp pick effort (default
// 4) — bumping to effort 6 yielded sub-1% byte savings against meaningfully longer encodes,
// noticeable in dev where every cold start re-runs ~240 transforms. Fallback JPEG q88 spends a
// few extra bytes on the rare non-AVIF browser, since they're already paying the larger file size.
//
// Query form — the literal string query is load-bearing. Passing widths through the object-form
// `query: { w: '480;960;...' }` makes Vite URL-encode the value, and imagetools-core v10 then
// emits only one source-width variant instead of splitting on `;` (verified empirically). String
// queries are preserved verbatim by Vite's glob transform and parsed correctly.
const avifMap = import.meta.glob('../images/{banners,home,team}/*.{jpg,jpeg,png}', {
  query: '?w=480;960;1440;1920;2560&format=avif&quality=85&as=srcset',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const fallbackMap = import.meta.glob('../images/{banners,home,team}/*.{jpg,jpeg,png}', {
  query: '?w=480;960;1440;1920;2560&quality=88&as=srcset',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const metaMap = import.meta.glob('../images/{banners,home,team}/*.{jpg,jpeg,png}', {
  query: '?w=1920&as=metadata',
  import: 'default',
  eager: true,
}) as Record<string, { width: number; height: number }>;

/**
 * A build-time-optimized image: an AVIF srcset, a same-format fallback srcset, and the intrinsic
 * dimensions of the largest variant (for aspect-ratio / CLS).
 */
export interface OptimizedImage {
  avif: string;
  fallback: string;
  width: number;
  height: number;
}

/**
 * Strip directory and extension from a glob path, leaving the filename stem used as the lookup key.
 */
function stem(path: string): string {
  const file = path.split('/').pop() ?? path;
  return file.replace(/\.[^.]+$/, '');
}

const registry: Record<string, OptimizedImage> = {};
for (const path of Object.keys(avifMap)) {
  const meta = metaMap[path];
  registry[stem(path)] = {
    avif: avifMap[path],
    fallback: fallbackMap[path],
    width: meta?.width ?? 0,
    height: meta?.height ?? 0,
  };
}

/**
 * Resolve a build-time-optimized image by its filename stem. Throws on an unknown key so a typo or
 * missing asset fails the build rather than silently rendering nothing.
 */
export function getImage(name: string): OptimizedImage {
  const img = registry[name];
  if (!img)
    throw new Error(
      `Unknown image key "${name}" — add the asset under src/images/{banners,home,team}/.`,
    );
  return img;
}
